class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
  }

  // Children are only guaranteed once we are connected — a custom element can be
  // upgraded before its own markup has been parsed, and then `facetForm` is null.
  connectedCallback() {
    const facetForm = this.querySelector('form');
    // Re-inserting the same element must not stack a second set of listeners.
    if (!facetForm || this.boundFacetForm === facetForm) return;
    this.boundFacetForm = facetForm;

    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById(
      'ProductCountDesktop',
    );
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGrid = document.getElementById('ProductGridContainer');
    const collection = productGrid && productGrid.querySelector('.collection');
    if (collection) collection.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function')
          initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch(() => {
        // The section request can simply not arrive — the shopper navigated
        // away mid-filter, or the connection dropped ("Load failed" on iOS
        // Safari, SECUNDO-STOREFRONT-36). Without a rejection handler that
        // became an unhandled rejection AND left the grid stuck under its
        // loading veil with the spinners still turning, because the only code
        // that cleared them ran in the success path. Leave the current results
        // on screen and take the veil off; the shopper can retry the filter.
        FacetFiltersForm.clearLoadingState();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function')
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  /**
   * Pull one element out of a fetched section response.
   *
   * What comes back is not guaranteed to be the section. Shopify can answer a
   * `?section_id=` request with an error or challenge page, an edge 5xx, or a
   * redirect body, and a cached entry can outlive the template it was rendered
   * from. Every renderer below used to dereference the lookup unconditionally,
   * so one bad response threw mid-re-render — SECUNDO-STOREFRONT-3B (fetch
   * path) and -3E (cache path, via the back button) are both this line.
   */
  static parseSection(html, id) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(id);
  }

  /**
   * Drop the loading veil and stop the spinners. Called on the success path and
   * on every bail-out, so a failed re-render can never leave the collection
   * greyed out for good.
   */
  static clearLoadingState() {
    const productGrid = document.getElementById('ProductGridContainer');
    const collection = productGrid && productGrid.querySelector('.collection');
    if (collection) collection.classList.remove('loading');

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const counter = document.getElementById(id);
      if (counter) counter.classList.remove('loading');
    });

    document
      .querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
      )
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderProductGridContainer(html) {
    const target = document.getElementById('ProductGridContainer');
    const source = FacetFiltersForm.parseSection(html, 'ProductGridContainer');
    if (!target || !source) {
      FacetFiltersForm.clearLoadingState();
      return;
    }

    target.innerHTML = source.innerHTML;

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const source = FacetFiltersForm.parseSection(html, 'ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (source && container) {
      container.innerHTML = source.innerHTML;
      if (containerDesktop) containerDesktop.innerHTML = source.innerHTML;
    }

    // Always clear, even when the count was unreadable: the veil and the
    // spinners are the shopper's only signal that filtering has finished.
    FacetFiltersForm.clearLoadingState();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      // The fetched section can carry a facet the live DOM doesn't have (a filter that only exists
      // for the new result set, or one that was dropped by an in-flight render), so this lookup can
      // legitimately miss. Skip it instead of throwing on `null.innerHTML`.
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      );
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest('.js-filter'),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById('FacetFiltersFormMobile')
      .closest('menu-drawer')
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden'),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace('hidden', 'show-more-item'),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form',
    );
    // This handler is debounced by 500ms, so a render kicked off by an earlier interaction can land
    // first and swap the facet markup out (renderFilters replaces .js-filter innerHTML). event.target
    // is then a detached node and closest('form') returns null — which threw on `.id` here and inside
    // the FormData() constructor in createSearchParams. Fall back to this component's own live form,
    // which holds the current checkbox state; if even that is gone, drop the stale event.
    const eventForm = event.target.closest('form') ?? this.querySelector('form');
    if (!eventForm) return;

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(eventForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = eventForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href;
    // A reset/remove link can point to a DIFFERENT collection than the current page — e.g. the
    // self-referential subcategory facet (mega-menu landing) escapes to /collections/all. renderPage
    // only swaps the query string onto window.location.pathname, so a cross-collection target would
    // be silently dropped and we'd re-render the same collection. Hard-navigate in that case.
    const targetPath = new URL(href, window.location.origin).pathname;
    if (targetPath !== window.location.pathname) {
      window.location.assign(href);
      return;
    }
    FacetFiltersForm.toggleActiveFacets();
    const url = href.indexOf('?') == -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  // Children are only guaranteed once we are connected — a custom element can be upgraded
  // before its own markup has been parsed, and then there are no inputs to read at all, so
  // setMinAndMaxValues() dereferenced an undefined max field and the whole price filter died
  // for that shopper. #78/#79 moved the rest of the theme's components off constructor-time
  // child lookups; this one sat between two of them and was missed.
  connectedCallback() {
    // Desktop applies manually (Enter / "Bruk"); mobile keeps auto-submit-while-typing and
    // has no button.
    this.applyButton = this.querySelector('.facets__apply');

    const inputs = this.querySelectorAll('input');
    if (!inputs.length) return;

    // Re-inserting the same element must not stack a second set of listeners.
    if (this.boundRangeInput === inputs[0]) return;
    this.boundRangeInput = inputs[0];

    const manual = Boolean(this.applyButton);

    inputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      // Desktop (manual): hold every change — typing AND the slider's own synthetic input
      // events — until "Bruk"/Enter, so the popover doesn't collapse mid-edit and the whole
      // range commits in one action. Mobile lets input bubble to Dawn's auto-submit as before.
      element.addEventListener('input', (event) => {
        if (!manual) return;
        event.stopPropagation();
        this.markDirty();
      });
      element.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.submitRange();
        }
      });
    });

    if (this.applyButton) {
      this.applyButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.submitRange();
      });
    }

    this.setMinAndMaxValues();
  }

  markDirty() {
    if (this.applyButton) this.applyButton.classList.add('is-dirty');
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  submitRange() {
    this.querySelectorAll('input').forEach((input) =>
      this.adjustToValidValues(input),
    );
    this.setMinAndMaxValues();

    const form = this.closest('form');
    const facetFiltersForm = this.closest('facet-filters-form');
    if (facetFiltersForm && form && facetFiltersForm.onSubmitHandler) {
      facetFiltersForm.onSubmitHandler({
        preventDefault: () => {},
        target: form,
        srcElement: { className: '' },
      });
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    // Reachable from onRangeChange/submitRange as well, i.e. from a listener whose input has
    // since been re-rendered out from under it by a facet refresh.
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  // Children are only guaranteed once we are connected — a custom element can be
  // upgraded before its own markup has been parsed, and then `facetLink` is null.
  connectedCallback() {
    const facetLink = this.querySelector('a');
    // Re-inserting the same element must not stack a second set of listeners.
    if (!facetLink || this.boundFacetLink === facetLink) return;
    this.boundFacetLink = facetLink;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class IntegerRange extends HTMLElement {
  constructor() {
    super();
    this.filterParam = this.dataset.filterParam;
    this.unit = this.dataset.unit || '';
    this.checkboxes = [];
    this.init();
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (!this.filterParam) {
      console.error('❌ Missing data-filter-param on integer-range');
      return;
    }

    // Find input fields
    this.fromInput = this.querySelector('.integer-from');
    this.toInput = this.querySelector('.integer-to');

    if (!this.fromInput || !this.toInput) {
      console.error('❌ Missing .integer-from or .integer-to inputs');
      return;
    }

    // Find checkboxes for this filter
    this.findCheckboxes();

    this.restoreUserInput();

    // Setup event listeners
    this.setupEventListeners();

    // Update status based on current state
    this.updateStatusMessage();
  }

  createStatusElement() {
    // Check if status element already exists
    if (this.querySelector('.integer-range-status')) return;

    // Create status element
    this.statusElement = document.createElement('div');
    this.statusElement.className = 'integer-range-status';
    this.statusElement.style.cssText = `
      margin-top: 8px;
      padding: 8px 12px;
      background: #f9f9f9;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
      text-align: center;
      line-height: 1.3;
      min-height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Insert after the inputs
    this.appendChild(this.statusElement);
  }



  updateStatusMessage() {
          const activeCheckboxes = this.checkboxes.filter((cb) => cb.checked);
          const userFrom = parseInt(this.fromInput.value.trim());
          const userTo = parseInt(this.toInput.value.trim());
          
          let shouldShowStatus = false;
          let message = '';
          let backgroundColor = '#f9f9f9';
          let textColor = '#666';
          let borderColor = '#e0e0e0';
          
          // hvis min er større enn max
          if (userFrom && userTo && userFrom > userTo) {
            shouldShowStatus = true;
            message = `Ugyldig intervall (prøv et bredere søk)`;
            backgroundColor = '#f8d7da';
            textColor = '#721c24';
            borderColor = '#f1b0b8';
          }
          // sjekker produkter i intervallet
          else if (userFrom && userTo && activeCheckboxes.length > 0) {
            const values = activeCheckboxes.map((cb) => parseInt(cb.value));
            const hasProductsInRange = values.some((v) => v >= userFrom && v <= userTo);
            
            if (!hasProductsInRange) {
              shouldShowStatus = true;
              message = `Ingen produkter i intervallet - prøv et bredere søk`;
              backgroundColor = '#fff3cd';
              textColor = '#856404';
              borderColor = '#ffc107';
            }
          }
          
          // Vis eller skjul status element
          if (shouldShowStatus) {
            this.createStatusElement();
            this.statusElement.textContent = message;
            this.statusElement.style.backgroundColor = backgroundColor;
            this.statusElement.style.color = textColor;
            this.statusElement.style.borderColor = borderColor;
            this.statusElement.style.display = 'flex';
          } else {
            // Skjul status element hvis det eksisterer
            if (this.statusElement) {
              this.statusElement.style.display = 'none';
            }
          }
      }

  restoreUserInput() {
    const urlParams = new URLSearchParams(window.location.search);

    const min = urlParams.get(`integer-range-${this.filterParam}-min`);
    if (min) {
      this.fromInput.value = min;
    } else if (this.filterParam.includes('total-quantity')) {
    // resetting quantity to 1 
    this.fromInput.value = 1;
  }
    const max = urlParams.get(`integer-range-${this.filterParam}-max`);
    if (max) {
      this.toInput.value = max;
    }
  }

 
  findCheckboxes() {
    // Find ALL checkboxes (including hidden ones) to avoid duplicates
    const allCheckboxes = Array.from(
      document.querySelectorAll(
        `input[name="${this.filterParam}"]:not([disabled])`,
      ),
    );

    this.checkboxes = allCheckboxes;
  }

  setupEventListeners() {

    if (this.listenersSetup) return;
    this.listenersSetup = true;

    // Desktop applies manually via Enter / "Bruk"; mobile and the single-field Antall keep
    // the original auto-submit-while-typing. In manual mode the two-field ranges
    // (Bredde/Høyde/Dybde) must NOT submit on every keystroke — that re-renders and collapses
    // the popover after "min", forcing a reopen to add "max".
    this.manualMode = !this.closest('.mobile-facets__details');
    this.applyButton = this.querySelector('.facets__apply');
    const autoSubmit =
      !this.manualMode || this.filterParam.includes('total-quantity');

    [this.fromInput, this.toInput].forEach((input) => {
      input.addEventListener('input', (e) => {
        // Keep input events from bubbling to Dawn's form-level auto-submit listener.
        e.stopPropagation();

        // Live validation feedback (invalid interval / no products) without submitting.
        this.updateStatusMessage();

        if (!autoSubmit) {
          // Surface that there are unapplied changes on this measurement's "Bruk".
          this.markDirty();
          return;
        }

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.processFilter(), 1000);
      });
    });

    // Enter commits this measurement, same as its "Bruk".
    [this.fromInput, this.toInput].forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          this.clearTimerAndProcess();
        }
      });
    });

    // "Bruk" below this measurement — apply this range.
    if (this.applyButton) {
      this.applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clearTimerAndProcess();
      });
    }

    if (this.filterParam.includes('total-quantity')) {
      const minusBtn = this.querySelector('.qty-minus');
      const plusBtn = this.querySelector('.qty-plus');
      
      if (minusBtn) {
          this.minusHandler = (e) => {
            this.adjustQuantity(-1);
          };
          minusBtn.addEventListener('click', this.minusHandler);
        }


      if (plusBtn) {
        this.plusHandler = (e)  => {
          this.adjustQuantity(1);
        };
        plusBtn.addEventListener('click', this.plusHandler);
      }
    }
  }

  markDirty() {
    if (this.applyButton) this.applyButton.classList.add('is-dirty');
  }

  adjustQuantity(change) {
      const min = parseInt(this.fromInput.min) || 0;
      const max = parseInt(this.fromInput.max) || 999;
      const current = parseInt(this.fromInput.value) || min;
      const newValue = Math.max(min, Math.min(max, current + change));
      
      this.fromInput.value = newValue;
      this.fromInput.dispatchEvent(new Event('input', { bubbles: false }));
  
    }


  clearTimerAndProcess() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.processFilter();
  }

  processFilter() {
   
    const fromVal = this.fromInput.value.trim();
    const toVal = this.toInput.value.trim();

    // Require BOTH fields to be filled
    if ((fromVal === '' && toVal !== '') || (fromVal !== '' && toVal === '')) {
      return;
    }

    // Skip if both fields are empty
    if (fromVal === '' && toVal === '') {
      this.resetToDefault();
      return;
    }

    const fromValue = parseInt(fromVal);
    const toValue = parseInt(toVal);

    // Validate numbers
    if (isNaN(fromValue) || isNaN(toValue)) {
      return;
    }

    // Check for matches BEFORE updating checkboxes
    const potentialMatches = this.checkForMatches(fromValue, toValue);

    this.updateCheckboxes(fromValue, toValue);
  }

  checkForMatches(minValue, maxValue) {
    const matches = [];

    this.checkboxes.forEach((checkbox) => {
      const checkboxValue = parseInt(checkbox.value);
      if (checkboxValue >= minValue && checkboxValue <= maxValue) {
        matches.push(checkboxValue);
      }
    });

    return matches;
  }

  resetToDefault() {
    // unchecker alle checkboxer
    let hasChanges = false;
    this.checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        hasChanges = true;
        checkbox.checked = false;
      }
    });

    if (hasChanges) {
      this.submitForm();
    }

    // Update status message
    this.updateStatusMessage();
  }

  submitForm() {
    // Find Shopify's facet form
    const facetForm = this.closest('form');
    if (!facetForm) {
      console.error('Could not find facet form');
      return;
    }

    // Find facet-filters-form element
    const facetFiltersForm = facetForm.closest('facet-filters-form');
    if (facetFiltersForm && facetFiltersForm.onSubmitHandler) {
      // Create fake event for Shopify's handler
      const fakeEvent = {
        preventDefault: () => {},
        target: facetForm,
        srcElement: { className: '' },
      };

      facetFiltersForm.onSubmitHandler(fakeEvent);
    } else {
      // Fallback: submit form directly
      facetForm.submit();
    }
  }

  updateCheckboxes(minValue, maxValue) {
    let hasChanges = false;

    // Update ALL checkboxes (both check and uncheck)
    this.checkboxes.forEach((checkbox) => {
      const checkboxValue = parseInt(checkbox.value);
      const shouldBeChecked =
        checkboxValue >= minValue && checkboxValue <= maxValue;

      // Check if state changes
      if (checkbox.checked !== shouldBeChecked) {
        hasChanges = true;
      }

      // Update checkbox state
      checkbox.checked = shouldBeChecked;
    });

    // Update status message FIRST (before form submission)
    this.updateStatusMessage();

    // Trigger form submission for server-side filtering
    if (hasChanges) {
      setTimeout(() => {
        this.submitShopifyForm();
      }, 100); // Small delay to let checkboxes update first
    } else {
      console.log(' No changes detected - skipping form submission');
    }
  }

  submitShopifyForm() {
    // Find Shopify's facet form
    const facetForm = this.closest('form');
    if (!facetForm) {
      console.error(' Could not find facet form');
      return;
    }

    // Find facet-filters-form element (Shopify's custom element)
    const facetFiltersForm = facetForm.closest('facet-filters-form');
    if (facetFiltersForm) {
      // Use Shopify's own submission handler
      if (facetFiltersForm.onSubmitHandler) {
        // Create fake event for Shopify's handler
        const fakeEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
          target: facetForm,
          srcElement: { className: '' },
        };
        facetFiltersForm.onSubmitHandler(fakeEvent);
      } else {
        this.directFormSubmit(facetForm);
      }
    } else {
      console.log(' facet-filters-form not found - trying direct submit');
      this.directFormSubmit(facetForm);
    }
  }

  directFormSubmit(form) {
    // Create FormData from form
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    // Update URL and reload
    const newURL = `${window.location.pathname}?${searchParams.toString()}`;
    window.location.href = newURL;
  }

  getFilterDisplayName() {
    // Get user-friendly name based on parameter
    if (this.filterParam.includes('width')) return 'Bredde';
    if (this.filterParam.includes('height')) return 'Høyde';
    if (this.filterParam.includes('depth')) return 'Dybde';
    if (this.filterParam.includes('total-quantity')) return 'Antall';
    return 'verdi'; // fallback
  }

 

}


// Register custom element
customElements.define('integer-range', IntegerRange);
